import {
  formatCodeableConcept,
  formatObservationValue as medplumFormatObservationValue,
  type WithId,
} from '@medplum/core';
import type { Coding, Observation } from '@medplum/fhirtypes';
import { parseISO } from 'date-fns';

const LOINC_SYSTEM = 'http://loinc.org';
const ACT_CODE_SYSTEM = 'http://terminology.hl7.org/CodeSystem/v3-ActCode';

/** Codings that assert a value without naming a topic. Rows carrying one must not be grouped with each other. */
const NON_SPECIFIC_CODINGS = new Set([`${ACT_CODE_SYSTEM}|ASSERTION`]);

/** Placeholder answers a source exports when the question was never asked. */
const UNINFORMATIVE_VALUE_CODES = new Set([
  '266927001', // Unknown if ever smoked
  '184115007', // Sex unknown
  'LA4489-6', // Unknown
  'LA12693-0', // Not sure
]);

export const getSocialHistoryTitle = (observation: Observation): string => {
  return formatCodeableConcept(observation.code) || observation.code?.coding?.[0]?.code || '';
};

export const formatSocialHistoryValue = (observation: Observation): string => {
  return medplumFormatObservationValue(observation) || '—';
};

/**
 * When a row's value was last asserted. Metriport consolidates identical observations across every
 * C-CDA it converts, so `effectivePeriod.end` is the last time that value was stated rather than the
 * end of a period of use. Imported rows almost never carry `effectiveDateTime`.
 */
export const getSocialHistoryEffectiveDate = (observation: Observation): string | undefined =>
  observation.effectivePeriod?.end ??
  observation.effectivePeriod?.start ??
  observation.effectiveDateTime ??
  observation.issued;

/** Undated rows sort last instead of poisoning the comparison with NaN. */
const effectiveTime = (observation: Observation): number => {
  const date = getSocialHistoryEffectiveDate(observation);
  if (!date) return Number.NEGATIVE_INFINITY;
  const time = parseISO(date).getTime();
  return Number.isNaN(time) ? Number.NEGATIVE_INFINITY : time;
};

const byRecencyDesc = (a: Observation, b: Observation): number => {
  const aTime = effectiveTime(a);
  const bTime = effectiveTime(b);
  return aTime === bTime ? 0 : bTime - aTime;
};

const isInformative = (observation: Observation): boolean =>
  !observation.valueCodeableConcept?.coding?.some((c) => c.code && UNINFORMATIVE_VALUE_CODES.has(c.code));

const codingToken = (coding: Coding): string => `${coding.system ?? ''}|${coding.code ?? ''}`;

/** The topic a row belongs to. Uncoded rows and bare assertions key off their own id so unrelated rows never merge. */
const getTopicKey = (observation: WithId<Observation>): string => {
  const codings = observation.code?.coding ?? [];
  const loinc = codings.find((c) => c.system === LOINC_SYSTEM && c.code)?.code;
  if (loinc) return `${LOINC_SYSTEM}|${loinc}`;
  const specific = codings.find((c) => c.code && !NON_SPECIFIC_CODINGS.has(codingToken(c)));
  return specific ? codingToken(specific) : observation.id;
};

/** A placeholder answer never displaces a real one, however recently it was asserted. */
const isPreferredOver = (candidate: Observation, incumbent: Observation): boolean => {
  const candidateIsInformative = isInformative(candidate);
  if (candidateIsInformative !== isInformative(incumbent)) return candidateIsInformative;
  return effectiveTime(candidate) > effectiveTime(incumbent);
};

/** Keep one row per social-history topic: the most recently asserted informative value. */
export const groupLatestByIdentifier = (observations: WithId<Observation>[]): WithId<Observation>[] => {
  const byTopic = new Map<string, WithId<Observation>>();
  for (const obs of observations) {
    const key = getTopicKey(obs);
    const incumbent = byTopic.get(key);
    if (!incumbent || isPreferredOver(obs, incumbent)) byTopic.set(key, obs);
  }
  return Array.from(byTopic.values()).sort(byRecencyDesc);
};

// Relocated to @liza/fhir-utils so the APIs can filter reads on the same list the add form writes;
// re-exported here to keep existing web import paths working.
export { SOCIAL_HISTORY_TOPICS } from '@liza/fhir-utils';
